/**
 * Consent-gated marketing analytics for the main site (thenotfit.com): Meta Pixel + Google Analytics 4.
 *
 * - Nothing loads until the visitor accepts cookies. Events tracked before that are held for the
 *   current page session and sent on accept; declining drops them.
 * - Tenant sites (subdomains / custom domains) never load either tag: members of a gym must not end
 *   up in THE NOT FIT's ad audiences.
 * - IDs come from env (REACT_APP_META_PIXEL_ID, REACT_APP_GA_MEASUREMENT_ID); with neither set this
 *   module is a no-op and the cookie banner stays hidden.
 */
import { getTenantFromSubdomain } from '@/lib/tenant';

const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || '';
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';

const CONSENT_KEY = 'tnf-cookie-consent';
const FBCLID_KEY = 'tnf-fbclid';

export type Consent = 'accepted' | 'declined';
export type EventParams = Record<string, string | number | boolean | undefined>;

/** Meta standard events we use; any other name is sent with trackCustom. */
const META_STANDARD_EVENTS = new Set([
  'PageView',
  'ViewContent',
  'Lead',
  'Contact',
  'InitiateCheckout',
  'CompleteRegistration',
]);

/** GA4 recommended names for the Meta events above; anything else is snake_cased. */
const GA_EVENT_NAMES: Record<string, string> = {
  ViewContent: 'view_content',
  Lead: 'generate_lead',
  Contact: 'contact',
  InitiateCheckout: 'begin_checkout',
  CompleteRegistration: 'sign_up',
};

type QueuedEvent = { name: string; params: EventParams; eventId?: string };

declare global {
  interface Window {
    fbq?: any;
    _fbq?: any;
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let loaded = false;
let pending: QueuedEvent[] = [];

/** True on the main marketing domain with at least one tag configured. */
export function analyticsEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return Boolean(META_PIXEL_ID || GA_MEASUREMENT_ID) && !getTenantFromSubdomain();
}

export function getConsent(): Consent | null {
  try {
    const value = localStorage.getItem(CONSENT_KEY);
    return value === 'accepted' || value === 'declined' ? value : null;
  } catch {
    return null;
  }
}

export function setConsent(value: Consent) {
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch {}
  if (value === 'accepted') {
    loadTags();
  } else {
    pending = [];
  }
}

/** Call once per page mount: remembers a Meta click ID and boots the tags if consent was given earlier. */
export function initMarketingAnalytics() {
  if (!analyticsEnabled()) return;
  rememberFbclid();
  if (getConsent() === 'accepted') loadTags();
}

/** Meta PageView for SPA navigations. GA4 tracks page views itself (enhanced measurement). */
export function trackPageView() {
  track('PageView');
}

/**
 * Track a conversion or engagement event on both tags.
 * Pass `eventId` for events a server may also send (Conversions API) so Meta can deduplicate.
 */
export function track(name: string, params: EventParams = {}, eventId?: string) {
  if (!analyticsEnabled()) return;
  const consent = getConsent();
  if (consent === 'declined') return;
  const event = { name, params: stripUndefined(params), eventId };
  if (!loaded) {
    pending.push(event);
    return;
  }
  send(event);
}

function send({ name, params, eventId }: QueuedEvent) {
  try {
    if (META_PIXEL_ID && window.fbq) {
      const method = META_STANDARD_EVENTS.has(name) ? 'track' : 'trackCustom';
      window.fbq(method, name, params, eventId ? { eventID: eventId } : undefined);
    }
    if (GA_MEASUREMENT_ID && window.gtag && name !== 'PageView') {
      window.gtag('event', GA_EVENT_NAMES[name] || toSnakeCase(name), params);
    }
  } catch {}
}

function loadTags() {
  if (loaded || !analyticsEnabled()) return;
  loaded = true;

  if (META_PIXEL_ID) {
    try {
      /* eslint-disable */
      (function (f: any, b: Document, e: string, v: string) {
        if (f.fbq) return;
        const n: any = (f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        });
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        const t = b.createElement(e) as HTMLScriptElement;
        t.async = true;
        t.src = v;
        const s = b.getElementsByTagName(e)[0];
        s.parentNode!.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
      /* eslint-enable */
      // We send PageView ourselves on route changes; stop the pixel from adding its own on pushState.
      window.fbq.disablePushState = true;
      restoreFbc();
      window.fbq('init', META_PIXEL_ID);
    } catch {}
  }

  if (GA_MEASUREMENT_ID) {
    try {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      document.head.appendChild(script);
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag() {
        // gtag.js expects the arguments object itself, not an array.
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer!.push(arguments);
      };
      window.gtag('js', new Date());
      window.gtag('config', GA_MEASUREMENT_ID);
    } catch {}
  }

  const queued = pending;
  pending = [];
  queued.forEach(send);
}

/**
 * Meta attributes ad clicks through the fbclid URL parameter, which the pixel turns into an `_fbc`
 * cookie. Visitors often accept cookies a page later (e.g. on /signup), after fbclid has left the
 * URL — so keep it for the session and write `_fbc` ourselves once consent is given.
 */
function rememberFbclid() {
  try {
    const fbclid = new URLSearchParams(window.location.search).get('fbclid');
    if (fbclid) sessionStorage.setItem(FBCLID_KEY, fbclid);
  } catch {}
}

function restoreFbc() {
  try {
    if (/(?:^|;\s*)_fbc=/.test(document.cookie)) return;
    const fbclid = sessionStorage.getItem(FBCLID_KEY);
    if (!fbclid) return;
    const maxAge = 90 * 24 * 60 * 60;
    document.cookie = `_fbc=fb.1.${Date.now()}.${fbclid}; path=/; max-age=${maxAge}; SameSite=Lax`;
  } catch {}
}

function stripUndefined(params: EventParams): EventParams {
  return Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined));
}

function toSnakeCase(name: string): string {
  return name.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
}
